<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";

import Button from "~/components/dumb/Button.vue";
import EmptyState from "~/components/dumb/EmptyState.vue";
import PageNotFound from "~/components/dumb/PageNotFound.vue";
import FilterArea from "~/components/filters/FilterArea.vue";
import TaskStatistics from "~/components/statistics/TaskStatistics.vue";
import { UpgradeRequiredIcon } from "~/icons";
import { goHomeNextTick, makeLinkToSettingsRef } from "~/router/common";
import { ButtonSize, ButtonStyle, Entitlement, Subscription, UserRole } from "~/shared/enums";
import type { Filter, StatisticConfig } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import { createOrUpdateSubscription, useUpgrade } from "~/utils/billing";

const props = defineProps<{
  dashboardDuid: string;
  slugSep?: string;
  slug?: string;
}>();

const router = useRouter();
const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const { getUpgradeDescription } = useUpgrade(tenantStore);

const isNotFound = ref(false);
const dashboard = computed(() => dataStore.getDashboardByDuid(props.dashboardDuid));
const taskStatisticsRef = ref<InstanceType<typeof TaskStatistics> | null>(null);
const hasDashboardsEntitlement = computed(() => tenantStore.getEntitlementValue(Entitlement.MAX_DASHBOARDS) > 0);
watchEffect(() => {
  if (!userStore.isRoleGte(UserRole.MEMBER)) {
    goHomeNextTick();
    return;
  }

  if (!dashboard.value) {
    isNotFound.value = true;
    pageStore.pageLoaded = true;
    return;
  }
  isNotFound.value = false;

  appStore.setCurrentPage(dashboard.value);
});

const redirectToBilling = () => router.push(makeLinkToSettingsRef("billing").value);
const upgradePlan = () => {
  createOrUpdateSubscription(Subscription.BUSINESS);
};

const onUpdateConfigs = async (configs: StatisticConfig[], loadData: boolean) => {
  if (!dashboard.value) {
    return;
  }

  await dataStore.updateDashboard(
    {
      duid: dashboard.value.duid,
      charts: configs,
    },
    { awaitBackend: true }
  );
  if (loadData) {
    taskStatisticsRef.value?.loadChartData();
  }
};

const setFilter = async (filter: Filter) => {
  // TODO incremental merge set and load into one call
  await appStore.setFilter(filter, { awaitBackend: true });
  taskStatisticsRef.value?.loadChartData(true);
};

const removeFilter = async (duid: string) => {
  await appStore.removeFilter(duid, { awaitBackend: true });
  taskStatisticsRef.value?.loadChartData(true);
};
</script>

<template>
  <div v-if="!isNotFound && dashboard" class="relative flex size-full flex-col items-center gap-3 pt-6">
    <FilterArea
      class="w-full max-w-[1048px] !border-0 px-[11px] bg-std"
      :class="{ 'pointer-events-none opacity-30 blur-sm': !hasDashboardsEntitlement }"
      is-search-mode
      :filters="appStore.filters"
      @set-filter="setFilter"
      @remove-filter="removeFilter" />
    <div class="flex size-full justify-center" :class="hasDashboardsEntitlement && 'overflow-y-scroll'">
      <div class="ml-[16px] size-full max-w-[1058px] extra-overscroll">
        <TaskStatistics
          ref="taskStatisticsRef"
          :class="{ 'pointer-events-none opacity-30 blur-sm': !hasDashboardsEntitlement }"
          :dashboard="dashboard"
          @update="onUpdateConfigs" />

        <EmptyState
          v-if="!hasDashboardsEntitlement"
          title="Upgrade to Business"
          :description="getUpgradeDescription('use dashboards', Subscription.BUSINESS)"
          :main-icon="UpgradeRequiredIcon"
          :button-icon="UpgradeRequiredIcon"
          button-text="Upgrade"
          @click="upgradePlan">
          <template #extraButton>
            <Button
              :btn-style="ButtonStyle.SECONDARY"
              text="See plans"
              :size="ButtonSize.SMALL"
              class="pointer-events-auto"
              @click="redirectToBilling" />
          </template>
        </EmptyState>
      </div>
    </div>
  </div>
  <PageNotFound v-else />
</template>
